import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useReducer
} from "react";
import {
  Box,
  debounce,
  Grid,
  styled
} from "@mui/material";

import CollapsableFormSection from "controls/global/collapsable-form-section";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import ActionButton from "controls/global/action-button";

import { ActionResult } from "entities/UIModel/ActionResult";
import { UWAProduct } from "entities/UIModel/UWAProduct";
import {
  fontSize,
  gaps,
  height,
  margin,
  padding
} from "theme/defaultStyle";
import useFieldDisabler from "utils/custom-hooks/useFieldDisabler";
import {
  OrderStatusType,
  ProductAction,
  ProductSectionTitle,
  ProgressImageType,
  ActionResultStatus,
  DocumentType,
  UIConstants,
  PERMISSIONS,
  UWARequestStatusType,
  ValidationStatus,
} from "utils/data/enum";
// import StewartAutocompleteWithCheckboxes from "controls/global/stewart-autocompletewithcheckboxes/StewartAutocompleteWithCheckboxes";
import TextInputField from "controls/global/text-input-field";
import CheckboxInputField from "controls/global/checkbox-field/CheckboxInputField";
import CustomTooltip from "controls/global/custom-tooltip";
import StyledInfoIcon from "theme/icons/StyledInfoIcon";
import CancelUndoCancelUWARequestDialog from './CancelUndoCancelUWARequestDialog';
import PdfViewer from "controls/global/pdf-viewer";
import UndoIcon from "@mui/icons-material/Undo";
import PrinterEyeIcon from "theme/icons/PrinterEyeIcon";
import DownloadIcon from '@mui/icons-material/Download';
import TitleExaminedFromDateField from "./TitleExaminedFromDateField";
import TitleExaminedToDateField from "./TitleExaminedToDateField";
import AnticipatedClosingDateField from "./AnticipatedClosingDateField";
import UWAPropertyTypeField from "./UWAPropertyTypeField";
import NotificationEmailAddresses from "./NotificationEmailAddressField";
import { showToast } from "controls/global/stewart-toast/showToast";
import ProposedPolicy from "./ProposedPolicyGrid/ProposedPolicy";
// import { SendMessageDocument } from "./SendMessageDocument";
import UploadDropZone from "controls/global/upload-files/UploadDropZone";
import WarningBanner from "controls/global/warning-banner";
// import { MultipleDownloadHeader } from "./MultipleDownloadHeader";
import UploadedDocumentsGrid from "./UploadedDocumentsGrid";
// import { SelectionConfig } from "controls/global/stewart-table/StewartTable";
import ConfirmationDialogWithProgressbar from "controls/global/dialogs/confirmation-dialog-with-progressbar";
import { useProcessStatusTracking } from "utils/context/ProcessStatusContext";
import {
  DialogRefType,
  FileUploadStatus,
  FileUploadAdditionalInfo,
  UWAPolicyInfo,
  PdfDocument,
} from "entities/UIModel";
import UploadFailedDialog from "./UploadFailedDialog";
// import ErrorDialog from "controls/global/dialogs/error-dialog";
import { Order } from "utils/sorting";
// import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { useGlobalAccess } from "utils/context/GlobalAccessContext";
import { useDocument } from "utils/context/DocumentContext";
import useCreateFile from "utils/custom-hooks/useCreateFile";
import { v4 as uuidv4 } from "uuid";
import {
  getFormattedErrorMessage,
  mapFileUploaResponse,
} from "pages/file/utils/fileUploadHelper";
import {
  FileRejection,
  FileWithPath
} from "react-dropzone";
import StewartLink from "controls/global/stewart-link";
import UWAMessagesGrid from "./UWAMessagesGrid";
import SpecialRisks from "./special-risks/SpecialRisks";
import { SendMessageDocument } from "./SendMessageDocument";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import ApprovalItemsSection from "./approval-item/ApprovalItemsSection";
import ProductStatus from "./ProductStatus";
import { useUWAGridStates } from "utils/context/UWAContext";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import { useMyFiles } from "utils/context/MyFilesContext";
import PreferredUnderwriterField from "./PreferredUnderwriterField";
import { UWADocument } from "entities/UIModel/UWADocument";
import { useLookup } from "utils/context/LookupContext";
import ReviseIcon from "theme/icons/ReviseIcon";
import * as UwaReducer from "pages/file/utils/uwaReducer";
import { ReviseAction } from "../jacket/JacketButtonSection";
import { cloneDeep } from "lodash";
import { useFilePage } from "utils/context/FilePageContext";
// import { Cancel } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import { useInitialValuesFiles } from "utils/context/FilesContext";
// import useUwa from "utils/custom-hooks/useUwa";
import { SelectFieldOption } from "controls/global/select-field/SelectInput";
// import { CancelIcon } from "theme/icons/CancelIcon";
import { getPdfDocument } from "../../../utils/helper";
import { CancelIconWithForbidden } from "theme/icons/CancelIconWithForbidden";
import useFocus from "utils/custom-hooks/useFocus";
import SpecialTextInputField from "./SpecialTextInputField";
import { useWatch } from "react-hook-form";

// import {
//   getBlobUrlForZipfile,
//   dateTimeAsFilename,
//   downloadZipFile,
// } from "utils/shared";
// import {
//   useUWAActions,
//   useUWAGridItems,
//   useUWAGridStates,
// } from "utils/context/UWAContext";

const GridContainer = styled(Box)({
  "& .MuiGrid-root.MuiGrid-item": {
    paddingBottom: `${padding.medium1} !important`,
  },
  "& .drop-zone": {
    marginTop: 0,
  },
  "& .button": {
    textAlign: "right",
  },
});

const TooltipGridItem = styled(Grid)({
  paddingLeft: `${padding.small1} !important`,
});

const TooltipContainer = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-end",
  paddingTop: padding.large2,
  gap: gaps.large1,
  "& .MuiInputBase-input": {
    paddingRight: padding.small2,
  },
  "& .MuiSvgIcon-root": {
    alignItems: "flex-end",
    display: "flex",
    height: "100%",
    marginRight: margin.zero,
    // paddingRight: padding.zero,
  },
});

const ButtonsContainer = styled(Box)({
  alignItems: "center",
  alignSelf: "center",
  display: "flex",
  flexDirection: "row",
  gap: gaps.small1,
  height: "100%",
  justifyContent: "flex-end",
  justifySelf: "flex-end",
});

const StyledTextField = styled(SpecialTextInputField)({
  "& .MuiInputBase-input": {
    fontSize: `${fontSize.large}`,
  },
});

const MULTI_SITE_INFO_MESSAGE =
  "Check Multi-Site/State if this transaction involves multiple properties. Please include additional property addresses, file numbers, or helpful information in the Summary field, or upload a spreadsheet or document with this information below.";
const MULTI_SITE_CHECKED_MESSAGE =
  "Please include additional property addresses, file numbers, or helpful information in the Summary field, or upload a spreadsheet or document in the Documents section.";
const PREFERRED_UW_INFO_MESSAGE =
  "Note, the Preferred Underwriter will be taken into consideration, but the assigned Owner will ultimately depend on availability.";
const MULTIPLE_EMAIL_INFO_MESSAGE =
  "For multiple email addresses, separate each one with a semicolon. ";
const UPLOAD_SUCCESS_UWA = "Document(s) successfully uploaded!";
const UPLOAD_FAIL_UWA = "Document(s) upload failed!";
const UPLOAD_FAIL_MESSAGE = "View failed documents";
const NO_RECORD_MESSAGE =
  "There are currently no UW documents uploaded to this product";
const FETCHING_DATA_ERROR_MESSAGE =
  "There was a problem fetching your documents. Please try again";
const STG_APPROVAL_WARNING_MESSAGE =
  "You must receive the email approval from STG Underwriting before proceeding.";

type UWAProps = {
  productIndex: number;
  uwaProduct: UWAProduct;
  submitButtonDisabled: boolean;
  onSubmit: (
    integrationKey: string,
    productAction?: string
  ) => Promise<ActionResult | undefined>;
  onDelete?: () => void;
};

export function UWA({
  productIndex,
  uwaProduct,
  submitButtonDisabled,
  onSubmit,
  onDelete,
}: UWAProps) {
  // const [key, setKey] = React.useState(0);
  // const forceRerender = () => setKey(prev => prev + 1);
  const disableDeleteButtom = useFieldDisabler("UWADeleteButton");
  const { setValue, getValues, clearErrors } = useFormWrapper();
  // const [, { setRevising }] = useInitialValuesFiles();
  const { reloadFile, reloadFileActivities, saveSCFile, resetValidationRequiredFlags } = useCreateFile();
  // const { setValueSpecialRisks } = useUwa();
  const { setFocusInputElement } = useFocus();

  // Permission
  const [{ profileSettings }] = useProfileSettingsCache();
  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  const hasSubmitPermission = permissionCodes?.includes(PERMISSIONS.UWA_SUBMIT);
  const hasRevisePermission = permissionCodes?.includes(PERMISSIONS.UWA_REVISE);
  const hasCancelPermission = permissionCodes?.includes(PERMISSIONS.UWA_CANCEL);
  const hasUndoCancelPermission = permissionCodes?.includes(PERMISSIONS.UWA_UNDOCANCEL);
  const hasViewRequestSummaryPermission = permissionCodes?.includes(PERMISSIONS.UWA_VIEWSUMMARY);
  // Reducer
  const [uwaState, dispatch] = useReducer(UwaReducer.reducer, UwaReducer.initialState);
  const { ACTIONS } = UwaReducer;
  // console.log("uwaState:", uwaState);

  // Functions and variables will be used in CollapsableFormSection
  const currentProduct: UWAProduct = getValues(`uwaProducts.${productIndex}`);
  const productStatusDisplay = `${currentProduct?.orderStatusTypeCode === OrderStatusType.Cancelled
    ? currentProduct.orderStatusTypeName + " " + (currentProduct?.cancelledDate?.substring(0, 10) ?? "")
    : currentProduct.orderStatusTypeName
    }`;
  const tabTitle = `${ProductSectionTitle.UWARequest} ${productIndex + 1}`;
  const getTabDescription = () => {
    // TODO - to be updated - need to add date at the end of the label based on order status
    const collapseLabel = currentProduct?.orderStatusTypeName || "";
    return collapseLabel;
  };
  const [openPdfViewer, setOpenPdfViewer] = useState<boolean>(false);
  const [pdfDocuments, setPdfDocuments] = useState<PdfDocument[]>([]);
  const [isStaticFile, setIsStaticFile] = useState(false);
  //#region - Variables to control the visible, hide, and disable for in TSX section
  const isProductStatusVisible: boolean = !!currentProduct?.orderStatusTypeCode && currentProduct?.orderStatusTypeCode !== OrderStatusType.Pending;
  const isSubmitVisible: boolean = !isProductStatusVisible;
  const submitAllowed = hasSubmitPermission;
  const isReviseVisible: boolean = isProductStatusVisible && !!currentProduct?.uwaRequest?.uwaRequestStatusTypeCode && currentProduct?.uwaRequest?.uwaRequestStatusTypeCode !== UWARequestStatusType.Cancelled;
  const reviseAllowed: boolean = hasRevisePermission && isReviseVisible; // !!currentProduct?.uwaRequest?.uwaRequestStatusTypeCode && currentProduct?.uwaRequest?.uwaRequestStatusTypeCode != UWARequestStatusType.Cancelled;
  const isCancelVisible: boolean = isProductStatusVisible && !!currentProduct?.uwaRequest?.uwaRequestStatusTypeCode && currentProduct?.uwaRequest?.uwaRequestStatusTypeCode !== UWARequestStatusType.Cancelled;
  const cancelAllowed: boolean = hasCancelPermission && isCancelVisible; // !!currentProduct?.uwaRequest?.uwaRequestStatusTypeCode && currentProduct?.uwaRequest?.uwaRequestStatusTypeCode === UWARequestStatusType.Cancelled;
  const isUndoCancelVisible: boolean = isProductStatusVisible && [OrderStatusType.Cancelled.toString()].includes(currentProduct?.orderStatusTypeCode);
  const undoCancelAllowed: boolean = hasUndoCancelPermission && (currentProduct?.uwaRequest?.uwaRequestStatusTypeCode && currentProduct?.uwaRequest?.uwaRequestStatusTypeCode === UWARequestStatusType.Cancelled ? true : false);
  const uwaRequestSummaryAllowed: boolean = hasViewRequestSummaryPermission;
  const isStgApprovalWarningVisible: boolean = ![
    OrderStatusType.Approved.toString(),
    OrderStatusType.ApprovedWithConditions.toString(),
    OrderStatusType.Cancelled.toString(),
    OrderStatusType.DeclineToInsure.toString(),
  ].includes(currentProduct?.orderStatusTypeCode);

  const isUndoCancelButtonDisabled = useFieldDisabler("UWAUndoCancellationButton");
  const isReviseButtonDisabled = useFieldDisabler("UWAReviseButton");
  const isSubmitRevisionButtonDisabled = useFieldDisabler("UWASubmitRevisionButton");

  // console.log("reviseAllowed", reviseAllowed);
  //  currentProduct?.uwaRequest?.uwaRequestStatusTypeName
  //#enregion

  //#region - Document Grid ---------------------
  // Functions and variables will be used within Document Grid, including edit the filename

  // --- Document Grid's Constants ---
  // const [, { updateSelectedUWAGridItem, updateSelectedUWAGridItems }] = useUWAActions();
  // const [{ selectedUWAGridItems }] = useUWAGridItems();
  const { generalConfig } = useConfigContext();
  const [, { setRequestTimeoutMs }] = useMyFiles();
  const [{ isUWAGridInEditMode }] = useUWAGridStates();
  const [data, setData] = useState<UWADocument[]>();
  const [userInputDebounceTimeMs, setUserInputDebounceTimeMs] = useState<number>(750);
  const [readyToSearch, setReadyToSearch] = useState<boolean>(false);
  const [partialFilterUpdate, setPartialFilterUpdate] = useState<boolean>(false);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const [, { uploadFiles, GetUwaDocumentListByOrder, getCPLRequestFormUrl, getUnderwriterRequestSummaryDocumentUrl }] = useDocument();
  const primaryProperty = useWatch({
    name: "properties.0",
  });

  const isFileDisabled = useFieldDisabler("AddUwaButton");

  const isAAL = primaryProperty?.state?.abbr === "NY";

  // const [isCancelDialogOpen, setCancelDialogOpen] = useState(false);
  // const [isUndoCancelDialogOpen, setUndoCancelDialogOpen] = useState(false);

  // --- Document Grid's Functions ---
  const loadSettings = async () => {
    const debounceTime = generalConfig.userInputDebounceTimeMs;
    const timeoutMs = generalConfig.filesSearchTimeoutMs;

    if (debounceTime) {
      setUserInputDebounceTimeMs(debounceTime);
    }
    if (timeoutMs) {
      setRequestTimeoutMs(timeoutMs);
    }
  };

  const debounceSetReadyToSearch = useCallback(
    debounce(() => setReadyToSearch(true), userInputDebounceTimeMs),
    [userInputDebounceTimeMs],
  );

  const canSearch = (): boolean => {
    return !partialFilterUpdate && filters !== undefined && Object.values(filters).length > 0 && !isUWAGridInEditMode;
  };

  const getDefaultFilters = (advanced: boolean): any => {
    // const sortColumn = "-documentCreatedDate";

    const defaultFilters: any = {
      fileId: getValues("id"),
      orderId: getValues(`uwaProducts.${productIndex}.orderID`),
      listCount: 0,
      requestedPage: 1,
      isInitialLoad: false,
      sortColumn: sortColumn,
    };
    return defaultFilters;
  };

  const getFileSearchCriteria = () => {
    let criteria: any;

    criteria = {
      ...filters,
      listCount: 0,
      requestedPage: 1,
      ...(globalAccessAgencyID && { companyId: globalAccessAgencyID }),
    };
    if (sortColumn) {
      criteria = {
        ...criteria,
        sortColumn: sortColumn,
      };
    }

    return criteria;
  };

  const performSearch = async () => {
    let criteria = getFileSearchCriteria();
    criteria.orderId = getValues(`uwaProducts.${productIndex}.orderID`);
    // criteria.showHidden = hasHideUnHideDocsAccess ? 1 : 0;
    if ((criteria.orderId || 0) > 0) {
      let documents: UWADocument[] | undefined = [];
      try {
        // TODO - rlo 10/21/2025 - tempoary commented until backend is fixed,
        // currently showing titlesearch document
        // documents = await getDocumentsByOrder({ ...criteria }, DocumentType.FILE);
        documents = await GetUwaDocumentListByOrder({ ...criteria });
        if (documents !== undefined) {
          setData(documents);
          if (!documents?.length) {
            setNoRecordsMessage([NO_RECORD_MESSAGE]);
            // setPage(1);
          }
          // setValue(
          //   "hasUploadedPolicy",
          //   documents && documents.length > 0 ? true : false
          // );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast({
          type: "error",
          title: FETCHING_DATA_ERROR_MESSAGE,
        });
      }
    }
  };


  //#endregion

  //#region Functions and variable for manage form states and elements
  //const isFieldDisabled = () => isProductStatusVisible; // && !reviseState.revising;
  const isFieldDisabled = isProductStatusVisible && !uwaState.revising;
  const isPreferredUnderWriterDisabld = isProductStatusVisible || isFileDisabled;

  const setNotDefault = () => {
    setValue(`uwaProducts.${productIndex}.isDefault`, false);
  };

  const handleIsMultiSiteChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (checked) {
      showToast({
        type: "warning",
        title: "Multi-Site/State checked!",
        message: MULTI_SITE_CHECKED_MESSAGE,
      });
    }
  };

  const onTitleExaminedFromChange = () => {
    setNotDefault();
    // TODO validate for To Date > From date
  };

  const onTitleExaminedToChange = () => {
    setNotDefault();
    // TODO validate for To Date > From date
  };

  //#endregion


  //#region Action's handlers (Submit, Revise, Cancel, Undo Cancel, etc)
  const [, { setRevising }] = useInitialValuesFiles();
  const [, { setRevisingMode }] = useFilePage();
  const [isCancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isUndoCancelDialogOpen, setUndoCancelDialogOpen] = useState(false);

  const handleSubmit = async () => {
    setNotDefault();
    onSubmit(currentProduct?.integrationKey, ProductAction.Issue);
  }

  async function handleSubmitRevise() {
    const actionResult = await onSubmit(
      currentProduct.integrationKey,
      ProductAction.Revise
    );

    if (actionResult?.validationStatus === ValidationStatus.InValid) {
      return;
    }

    setRevisingMode(false);

    dispatch({ type: ACTIONS.SUBMIT_REVISE, productIndex: productIndex });
    setRevising(false);
  }

  const handleRevise = async (action: ReviseAction) => {
    // setNotDefault();
    // onSubmit(currentProduct.integrationKey, ProductAction.Revise);
    // const { ACTIONS } = UwaReducer;
    const currentUwaProduct: UWAProduct = getValues(`uwaProducts.${productIndex}`);

    switch (action) {
      case ReviseAction.OpenRevise: {
        setRevisingMode(true);

        setValue("previousReviseProduct", cloneDeep(currentUwaProduct));
        dispatch({
          type: ACTIONS.START_REVISE,
          payload: cloneDeep(currentUwaProduct),
          productIndex: productIndex,
        });
        setRevising(true);

        // Add a blank Proposed Policy
        let newUwaPolicyInfos: UWAPolicyInfo[] = [];
        if (currentUwaProduct?.uwaPolicyInfos && currentUwaProduct?.uwaPolicyInfos.length > 0) {
          newUwaPolicyInfos = cloneDeep(currentUwaProduct?.uwaPolicyInfos);
        }

        const emptyPolicyInfo: UWAPolicyInfo = {
          identityKey: 0,
          integrationKey: uuidv4(),
          uwaPolicyInfoID: 0,
          uwaRequestID: currentUwaProduct?.uwaRequest?.uwaRequestID || 0,
          uwaPolicyTypeCode: undefined,
          policyForm: undefined,
          policyAmount: undefined,
          proposedInsured: undefined,
          endorsements: undefined,

          id: crypto.randomUUID(),
        }

        newUwaPolicyInfos?.push(emptyPolicyInfo);
        setValue(
          `uwaProducts.${productIndex}.uwaPolicyInfos`,
          newUwaPolicyInfos
        );


        // Add a blank Special Risk
        let newSpecialRisks: SelectFieldOption[] = [];
        if (currentProduct?.uwaRequestInfo?.specialRisks && currentProduct?.uwaRequestInfo?.specialRisks?.length > 0) {
          newSpecialRisks = cloneDeep(currentProduct?.uwaRequestInfo?.specialRisks);
        }

        //const specialRiskLastIndex = currentProduct?.uwaRequestInfo?.specialRisks?.length || 0;
        const emptySpecialRisk: SelectFieldOption = {
          text: "",
          value: "",
        }
        //setValueSpecialRisks(`uwaProducts.${productIndex}.uwaRequestInfo.specialRisks`, specialRiskLastIndex, emptySpecialRisk);
        newSpecialRisks.push(emptySpecialRisk);

        setValue(
          `uwaProducts.${productIndex}.uwaRequestInfo.specialRisks`,
          newSpecialRisks
        );

        setValue(`uwaProducts.${productIndex}.isRevising`, true);

        break;
      }
      case ReviseAction.SubmitRevise: {
        await handleSubmitRevise();
        break;
      }
      case ReviseAction.CancelRevise: {

        setRevisingMode(false);
        setValue(`uwaProducts.${productIndex}.isRevising`, false);
        clearErrors(`uwaProducts.${productIndex}` as const);
        resetValidationRequiredFlags();
        setValue(
          `uwaProducts.${productIndex}`,
          uwaState.previousProduct
        );

        dispatch({ type: ACTIONS.CANCEL_REVISE, productIndex: productIndex });
        setRevising(false);

        break;
      }
      default:
        break;
    }

  }

  const handleCancelSubmit = () => {
    setNotDefault();
    onSubmit(currentProduct.integrationKey, ProductAction.Void);
    setCancelDialogOpen(false);
  }

  const handleUndoCancelSubmit = () => {
    setNotDefault();
    onSubmit(currentProduct.integrationKey, ProductAction.UndoVoid);
    setUndoCancelDialogOpen(false);
  };

  const handleViewRequestSummary = async () => {
    const actionResult: ActionResult = await getUnderwriterRequestSummaryDocumentUrl(
      getValues(`uwaProducts.${productIndex}.orderID`),
      getValues(`uwaProducts.${productIndex}.fileID`),
      uuidv4()
    );

    setPdfDocuments([
      getPdfDocument(
        undefined,
        undefined,
        undefined,
        actionResult.documentId || 0,
        actionResult.documentInfo,
        actionResult.documentName || "UWARequestSummary",
        0
      ),
    ]);
    setIsStaticFile(false);
    setOpenPdfViewer(true);
  }
  const handleCPLRequestForm = async () => {
    const actionResult: ActionResult = await getCPLRequestFormUrl();

    setPdfDocuments([
      getPdfDocument(
        undefined,
        undefined,
        undefined,
        actionResult.documentId || 0,
        actionResult.documentInfo,
        actionResult.documentName || "CPL Request Form",
        0
      ),
    ]);
    setIsStaticFile(true);
    setOpenPdfViewer(true);
  }
  //#endregion


  const InitialMessage = "Loading results...";
  // const processedStatusTypeCode: string | undefined =
  //   currentProduct?.processedStatusTypeCode;
  // const orderID = currentProduct?.orderID || 0;
  // console.log("currentProduct:", currentProduct);



  // upload states
  const [uploadCompleted, setUploadCompleted] = useState<boolean>(false);

  const isProgressCompletedRef = useRef<boolean>(false);
  const successUploadFilesRef = useRef<FileUploadStatus[]>([]);
  const failedUploadFilesRef = useRef<FileUploadStatus[]>([]);

  // control show progress
  const [, { setBypassDefaultLoader, setPercentageComplete }] =
    useProcessStatusTracking();
  const [openProgressDialog, setOpenProgressDialog] = useState<boolean>(false);
  const [runProgress, setRunProgress] = useState<boolean>(false);
  const [progressRequestId, setProgressRequestId] = useState<string>("");

  // show upload failed dialog
  const uploadFailedDialogRef = useRef<DialogRefType>(null);
  const [, { getApprovalItemsByUWARequest }] = useLookup();

  // Error dialog
  // const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);
  // const [errorMessage, setErrorMessage] = useState<string | ReactNode>("");
  // const [correlationId, setCorrelationId] = useState<string>("");

  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof UWADocument | undefined>(
    "documentCreatedDate"
  );
  const [noRecordsMessage, setNoRecordsMessage] = useState<string[]>([
    InitialMessage,
  ]);
  const [sortColumn, setSortColumn] = useState<string | undefined>(
    "-documentCreatedDate"
  );

  // const [{ profileSettings }] = useProfileSettingsCache();

  // const permissionCodes = profileSettings?.userPermissionItemTypes as string[];

  const [filters, setFilters] = useState<any | undefined>(undefined);
  const [uwaActivities, setUwaActivities] = useState<any>({});


  const request = {
    createdFromMainPortal: true,
    status: "Approved", // or "Approved with Conditions"
  };

  // Global Access Agency
  const [{ selectedAgency: globalAccessAgency }] = useGlobalAccess();
  const globalAccessAgencyID = globalAccessAgency?.[0]?.CompanyID;




  const showUploadFailedToast = () => {
    const handleLinkClick = () => {
      uploadFailedDialogRef.current?.open();
    };

    const linkMessage = () => {
      return (
        <StewartLink onClick={() => handleLinkClick()}>
          {UPLOAD_FAIL_MESSAGE}
        </StewartLink>
      );
    };

    showToast({
      type: "error",
      title: UPLOAD_FAIL_UWA,
      message: linkMessage(), // "The Property Type for your UW differs from the file Property Type. Please scroll to the top of the file to update."
    });
  };



  const handleOnUpload = async (
    acceptedFiles: FileWithPath[],
    rejectedFiles: FileRejection[]
  ) => {
    // -- Show upload in progress
    setUploadCompleted(false);
    const progressRequestId = uuidv4();
    setProgressRequestId(progressRequestId);
    setBypassDefaultLoader(true);
    setOpenProgressDialog(true);
    setRunProgress(true);

    isProgressCompletedRef.current = false;

    try {
      // Save file if file has not saved yet
      let fileId: number = getValues("id") || 0;
      let orderId: number = getValues(`uwaProducts.${productIndex}.orderID`) || 0;
      let uwaRequestID: number = getValues(`uwaProducts.${productIndex}.uwaRequest.uwaRequestID`) || 0;

      if (fileId === 0 || orderId === 0 || uwaRequestID === 0) {
        // set UW Product to Dirty because it also need to save UW Products first
        setNotDefault();
        const actionResult = await saveSCFile();
        fileId = actionResult?.scfile?.id || 0;
        orderId = actionResult?.scfile?.uwaProducts[productIndex].orderID || 0;
        uwaRequestID = actionResult?.scfile?.uwaProducts[productIndex].uwaRequest?.uwaRequestID || 0;
      }
      if (fileId === 0) {
        setPercentageComplete(progressRequestId);
        setUploadCompleted(true);
        return;
      }

      // -- Prepare for upload
      let fileAdditionalInfos: FileUploadAdditionalInfo[] = [];
      const formData = new FormData();

      const uploadMessage: string = getValues("uploadMessage") || "";
      const agency = getValues("agency");
      const companyID = agency?.id || "";
      formData.set("companyID", companyID);
      formData.set("fileID", fileId.toString());
      formData.set("orderID", orderId.toString());
      formData.set("uwaRequestID", uwaRequestID.toString());
      // no messageID needed -- it will be always a new message for upload with message
      formData.set("messageText", uploadMessage);
      // formData.set("processedStatusTypeCode", processedStatusTypeCode || "");

      // TEST ONLY DRAFT mode
      // formData.set("processedStatusTypeCode", TitleSearchProcessedStatusType.Dropped2SB);
      // formData.set("uploadSessionKey", uploadSessoinRef.current);

      // TEMPORARY ONLY needed for Document Suplementatal
      const clientFileID = getValues("fileNameNumber") || "";
      // const agency = getValues("agency");
      // const companyID = agency?.id || "";
      const companyLegacyID = agency?.legacyID || "";
      formData.set("clientFileID", clientFileID);
      // formData.set("companyID", companyID);
      formData.set("companyLegacyID", companyLegacyID);
      // END TEMPORARY

      let index = 0;
      acceptedFiles?.forEach((file) => {
        formData.append("files", file, file.name);
        fileAdditionalInfos.push({
          index: index,
          filePath: file.path || "",
          fileName: file.name,
          fileSize: file.size,
          errorMessage: "",
          isValidFile: true,
        });
        index++;
      });

      rejectedFiles?.forEach((rejectedFile, i) => {
        fileAdditionalInfos.push({
          index: index,
          filePath: "", // not available
          fileName: rejectedFile.file.name,
          fileSize: rejectedFile.file.size,
          errorMessage: getFormattedErrorMessage(rejectedFile.errors),
          isValidFile: false,
        });
        index++;
      });

      formData.append(
        "fileUploadAdditionalInfo",
        JSON.stringify(fileAdditionalInfos)
      );

      // -- Start upload files
      const actionResult: ActionResult = await uploadFiles(
        progressRequestId,
        formData,
        DocumentType.FILE
      ); // DocumentType.FILE);

      if (actionResult.status === ActionResultStatus.Success) {
        const uploadedResult = actionResult.apiResponse;
        const fileUploadStatuses = mapFileUploaResponse(uploadedResult);

        // -- show upload result status
        const hasSuccess = fileUploadStatuses?.some((f) => f.isUploadSuccess);
        const hasFailed = fileUploadStatuses?.some((f) => !f.isUploadSuccess);
        successUploadFilesRef.current = fileUploadStatuses?.filter(
          (f) => f.isUploadSuccess
        );
        failedUploadFilesRef.current = fileUploadStatuses?.filter(
          (f) => !f.isUploadSuccess
        );

        if (hasFailed) {
          showUploadFailedToast();
        }
        if (hasSuccess) {
          showToast({
            type: "success",
            title: UPLOAD_SUCCESS_UWA,
          });

          // reset order by DocumentCreatedDate
          setOrder("desc");
          setOrderBy("documentCreatedDate" as keyof UWADocument);
          setSortColumn("-documentCreatedDate");
          await performSearch();
        }
      } else {
        showToast({
          type: "error",
          title: UIConstants.UNEXPECTED_ERROR_MESSAGE.toString(),
        });
      }

      setPercentageComplete(progressRequestId);
      setUploadCompleted(true);
    } catch (err) {
      setPercentageComplete(progressRequestId);
      setUploadCompleted(true);
      // console.log("upload error:", err)
      showToast({
        type: "error",
        title: UPLOAD_FAIL_UWA,
      });
    }
  };
  // Upload related function
  const handleOnCloseProgressDialog = async () => {
    isProgressCompletedRef.current = true;
    if (uploadCompleted) {
      setOpenProgressDialog(false);
      setRunProgress(false);
      setProgressRequestId("");
    }
  };






  const handleRequestSort = async (
    columnName: any,
    disableToggling: boolean = false
  ) => {
    let sortBy = columnName; // getDatabaseSortColumnName(columnName);
    if (!disableToggling && sortColumn === sortBy) {
      sortBy = `-${sortBy}`;
    }

    setOrder(sortBy.startsWith("-") ? "desc" : "asc");
    setOrderBy(columnName.replace("-", "") as keyof UWADocument);
    setSortColumn(sortBy);
  };

  // const handleOnYesErrorDialog = () => {
  //   setOpenErrorDialog(false);
  // };

  const handleOnReloadFile = async () => {
    const fileId = getValues("id");
    await reloadFile(fileId); // happen after Save File
  };

  const handleOnReloadFileActivities = async () => {
    const fileId = getValues("id");
    await reloadFileActivities(fileId); // happen after Save File
  };

  const handleOnSaveFile = async () => {
    await saveSCFile();
  };

  //Please put any useEffect within this section
  //#region - useEffect section
  // --- Document Grid useFeect ---
  useEffect(() => {
    const init = async () => {
      await loadSettings();
      setSettingsLoaded(true);
    };
    init();
  }, []);

  useEffect(() => {
    const defaultFilters = getDefaultFilters(false);
    setFilters(defaultFilters);
  }, []);

  useEffect(() => {
    const init = async () => {

      const response = await getApprovalItemsByUWARequest(
        currentProduct?.uwaRequest?.uwaRequestID
      );
      if (response?.apiResponse) {
        setUwaActivities(response.apiResponse);
      }
    };

    if (currentProduct?.uwaRequest?.uwaRequestID && currentProduct?.uwaRequest?.uwaRequestID !== 0) {
      init();
    }
  }, []);


  useEffect(() => {
    if (readyToSearch) {
      const search = async () => {
        await performSearch();
        setReadyToSearch(false);
      };

      search();
    }
  }, [readyToSearch]);

  useEffect(() => {
    if (canSearch()) {
      if (partialFilterUpdate) {
        setPartialFilterUpdate(false);
      }

      debounceSetReadyToSearch();
    }
  }, [
    filters,
    sortColumn,
    settingsLoaded,
    uwaActivities
  ]);

  // --- File upload useEffect ---
  useEffect(() => {
    if (uploadCompleted && openProgressDialog) {
      handleOnCloseProgressDialog();
    }
  }, [uploadCompleted]);

  // -- Setfocus on Property Type field
  useEffect(() => {
    setFocusInputElement(`uwaProducts.${productIndex}.uwaRequest.propertyTypes`);
  }, [])

  // --- Alway run when component unmounts
  useEffect(() => {
    setValue(`uwaProducts.${productIndex}.isRevising`, false);
    setValue("previousReviseProduct", null);

    return () => {
      // console.log("Component unmounted");
      const isCurrentlyRevising = !!getValues(`uwaProducts.${productIndex}.isRevising`);
      const previousReviseProduct = getValues("previousReviseProduct");

      if (isCurrentlyRevising) {
        setValue(
          `uwaProducts.${productIndex}`,
          previousReviseProduct
        );
        setRevisingMode(false);
        setRevising(false);
      }
    }
  }, [])

  //#endregion
  // console.log("initialValues:", initialValues);
  // console.log("current Product:", currentProduct);
  //#region TSX section
  return (
    <>
      <CollapsableFormSection
        // key={key}
        index={productIndex}
        showDelete={!disableDeleteButtom && !isProductStatusVisible}
        onDelete={onDelete}
        label={getTabDescription()}
        title={tabTitle}
        isProductIssuePending={
          uwaProduct?.orderStatusTypeCode === OrderStatusType.Pending
        }
      >
        <GridContainer>
          <Grid container={true} spacing={3}>
            {isProductStatusVisible && (
              <ProductStatus
                submittedDate={currentProduct?.submittedDate || ""}
                ownerName={currentProduct?.uwaRequest?.ownerName || ""}
                uwaRequestNumber={currentProduct?.uwaRequest?.uwaRequestNumber || ""}
                uwaRequestStatusTypeName={productStatusDisplay || ""}
              />
            )}
            {/* Row 1 - normal screen size */}
            <Grid item={true} xs={12} sm={6} md={3} lg={3} xl={3}>
              <UWAPropertyTypeField
                name={`uwaProducts.${productIndex}.uwaRequest.propertyTypes`}
                disabled={isFieldDisabled}
                onChange={onTitleExaminedFromChange}
              />
            </Grid>
            <Grid item={true} xs={12} sm={6} md={3} lg={3} xl={3}>
              <AnticipatedClosingDateField
                name={`uwaProducts.${productIndex}.uwaRequest.anticipatedClosingDate`}
                disabled={isFieldDisabled}
                onChange={onTitleExaminedFromChange}
                defaultValue={undefined}
              />
            </Grid>
            <Grid item={true} xs={12} sm={6} md={6} lg={6} xl={6}>
              <TextInputField
                label="Project Name/Reference"
                name={`uwaProducts.${productIndex}.uwaRequest.projectReferenceNumber`}
                onChange={setNotDefault}
                disabled={isFieldDisabled}
                maxLength={500}
              />
            </Grid>

            {/* Row 2 - normal screen size */}
            <Grid item={true} xs={12} sm={6} md={3} lg={3} xl={3}>
              <TitleExaminedFromDateField
                name={`uwaProducts.${productIndex}.uwaRequest.titleExaminedFrom`}
                disabled={isFieldDisabled}
                onChange={onTitleExaminedFromChange}
                defaultValue={undefined}
              />
            </Grid>
            <Grid item={true} xs={12} sm={6} md={3} lg={3} xl={3}>
              <TitleExaminedToDateField
                name={`uwaProducts.${productIndex}.uwaRequest.titleExaminedTo`}
                disabled={isFieldDisabled}
                onChange={onTitleExaminedToChange}
                defaultValue={undefined}
              />
            </Grid>

            <Grid item={true} xs={12} sm={6} md={3.4} lg={3.4} xl={4.5}>
              <TextInputField
                label="Prior Approval #"
                name={`uwaProducts.${productIndex}.uwaRequest.priorApprovalNumber`}
                onChange={setNotDefault}
                disabled={isFieldDisabled}
                maxLength={500}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={2.6}
              lg={2.6}
              xl={1.5}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <CheckboxInputField
                label="Multi-Site/State"
                name={`uwaProducts.${productIndex}.uwaRequestInfo.isMultiSite`}
                disabled={isFieldDisabled}
                onChange={handleIsMultiSiteChange}
              />
              <CustomTooltip
                title={MULTI_SITE_INFO_MESSAGE}
                placement="left"
                aria-label="multi-site-info"
                arrow
              >

                <StyledInfoIcon />
              </CustomTooltip>
            </Grid>

            {/* Row 3 - normal screen size */}
            <Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
              <StyledTextField
                label="Summary of transaction, property, legal authority (e.g. power of attorney), etc. "
                name={`uwaProducts.${productIndex}.uwaRequest.summary`}
                multiline
                errorMessageExceedLimit={"Cannot exceed 500 characters"}
                onChange={setNotDefault}
                disabled={isFieldDisabled}
                validateOnChange
                errorMaxLength={500}
              />
            </Grid>
            {/* Proposed Policy */}
            <Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
              <ProposedPolicy
                schema={`uwaProducts.${productIndex}.uwaPolicyInfos`}
                disabled={isFieldDisabled || isFileDisabled}
                existingUwaPolicyInfos={currentProduct?.uwaPolicyInfos}
                submitted={isProductStatusVisible}
                revising={uwaState.revising}
              />
            </Grid>
            {/* Special Risks */}
            <Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
              <SpecialRisks
                schema={`uwaProducts.${productIndex}`}
                disabled={isFieldDisabled || isFileDisabled}
                existingSpecialRisks={currentProduct?.uwaRequestInfo?.specialRisks || []}
                revising={uwaState.revising}
              />
            </Grid>
            {/* Approval Items */}
            {<Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
              <ApprovalItemsSection
                createdFromMainPortal={request.createdFromMainPortal}
                status={request.status}
                activities={uwaActivities}
              />
            </Grid>}
            {/* Row 4 - below Special Risks */}
            <Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
              <Grid container={true} spacing={3} alignItems="flex-start" sx={{
                overflow: "visible",
                "& .MuiGrid-item": {
                  overflow: "visible",
                  paddingTop: `${padding.xsmall}`,
                  paddingBottom: `${padding.xsmall}`,
                }
              }}>
                <Grid item={true} xs={6} sm={5.5} md={3.6} lg={3.6} xl={3.6} sx={{
                  overflow: "visible",
                  minHeight: `${height.xxsmall}`,
                }}>
                  <PreferredUnderwriterField
                    schema={`uwaProducts.${productIndex}.uwaRequest`}
                    name={`uwaProducts.${productIndex}.uwaRequest.preferredUnderwriterName`}
                    disabled={isPreferredUnderWriterDisabld}
                    onChange={setNotDefault}
                  />
                </Grid>
                <TooltipGridItem item={true} xs={0.4} sm={0.4} md={0.4} lg={0.4} xl={0.4}>
                  <TooltipContainer>
                    <CustomTooltip
                      title={PREFERRED_UW_INFO_MESSAGE}
                      placement="left"
                      aria-label="preferred_uw_info"
                      arrow
                    >
                      <StyledInfoIcon />
                    </CustomTooltip>
                  </TooltipContainer>
                </TooltipGridItem>

                <Grid item={true} xs={5.6} sm={5.6} md={7.6} lg={7.6} xl={7.6} sx={{ overflow: "visible" }}>
                  <NotificationEmailAddresses
                    schema={`uwaProducts.${productIndex}`}
                    name={`uwaProducts.${productIndex}.notificationEmailAddresses`}
                    productIndex={productIndex}
                    disabled={isFieldDisabled}
                  />
                </Grid>

                <TooltipGridItem item={true} xs={0.4} sm={0.4} md={0.4} lg={0.4} xl={0.4}>
                  <TooltipContainer>
                    <CustomTooltip
                      title={MULTIPLE_EMAIL_INFO_MESSAGE}
                      placement="left"
                      aria-label="multiple_email-info"
                      arrow
                    >
                      <StyledInfoIcon />
                    </CustomTooltip>
                  </TooltipContainer>
                </TooltipGridItem>
              </Grid>
            </Grid>

            {!isProductStatusVisible && (
              <Grid item={true} xs={12} sm={12} md={12} lg={12} xl={12}>
                <StyledTextField
                  label="Message"
                  name={`uwaProducts.${productIndex}.messages.0.messageText`}
                  onChange={setNotDefault}
                  disabled={isFieldDisabled}
                  multiline
                  errorMessageExceedLimit={"Cannot exceed 2500 characters"}
                  validateOnChange
                  errorMaxLength={2500}
                />
              </Grid>
            )}

            {isProductStatusVisible && (
              <UWAMessagesGrid productIndex={productIndex} />
            )}

            {!isProductStatusVisible && (
              <Grid item={true} xs={12} md={12} lg={12} xl={12}>
                <UploadDropZone
                  documentType={DocumentType.FILE}
                  disabled={isUWAGridInEditMode || isFileDisabled}
                  handleOnUpload={handleOnUpload}
                />
              </Grid>
            )}
            {data && data.length > 0 && (
              <Grid rowSpacing={2} item={true} xs={12} md={12} lg={12} xl={12}>
                {/* {selectedUWAGridItems?.filter((d) => d.orderID === orderID)?.length > 0 && (
                  <MultipleDownloadHeader orderID={orderID} onDownload={handleDownloadZipfile} />
                )} */}
                <UploadedDocumentsGrid
                  // selectionConfig={selectionConfig}
                  orderStatusTypeCode={currentProduct?.orderStatusTypeCode}
                  page={1}
                  rows={data ? data : []}
                  orderBy={orderBy}
                  order={order}
                  noRowsMessage={noRecordsMessage}
                  onRequestSort={handleRequestSort}
                  performSearch={performSearch}
                  reloadFile={handleOnReloadFile}
                  reloadFileActivities={handleOnReloadFileActivities}
                  saveFile={handleOnSaveFile}
                />
              </Grid>
            )}
            <Grid rowSpacing={2} item={true} xs={12} md={12} lg={12} xl={12}>
              {permissionCodes?.includes(PERMISSIONS.UWA_SENDMESSAGEDOC) && isProductStatusVisible &&
                <SendMessageDocument
                  productIndex={productIndex}
                  saveFile={handleOnSaveFile}
                  performSearch={performSearch}
                  reloadFile={handleOnReloadFile}
                  reloadFileActivities={handleOnReloadFileActivities}
                  revising={uwaState.revising}
                  disabled={isFileDisabled}
                />
              }
            </Grid>
            {isStgApprovalWarningVisible && (
              <Grid item={true} xs={12} md={12} lg={12} xl={12}>
                <WarningBanner message={STG_APPROVAL_WARNING_MESSAGE} />
              </Grid>
            )}


            {/* Action buttons container */}
            <Grid item={true} xs={12} md={12} lg={12} xl={12}>
              <ButtonsContainer>
                { !isAAL  && <ActionButton
                  color="secondary"
                  onClick={handleCPLRequestForm}
                  startIcon={<DownloadIcon />}
                  disabled={isFileDisabled}
                  variant="outlined"
                >
                  CPL Request Form
                </ActionButton>}
                {isSubmitVisible && (
                  <ActionButton
                    color="primary"
                    disabled={
                      submitButtonDisabled ||
                      !submitAllowed ||
                      isProductStatusVisible
                    }
                    onClick={handleSubmit}
                    variant="outlined"
                  >
                    Submit
                  </ActionButton>
                )}
                {isCancelVisible && !uwaState.revising && (
                  <>
                    <ActionButton
                      color="secondary"
                      disabled={!cancelAllowed || isFileDisabled}
                      onClick={() => setCancelDialogOpen(true)}
                      startIcon={<CancelIconWithForbidden />}
                      variant="outlined"
                    >
                      Cancel
                    </ActionButton>

                    <CancelUndoCancelUWARequestDialog
                      title="Cancel Underwriting Request?"
                      buttonText="Cancel Request"
                      schema={`uwaProducts.${productIndex}`}
                      onSubmit={handleCancelSubmit}
                      isOpen={isCancelDialogOpen}
                      onClose={() => {
                        setCancelDialogOpen(false);
                      }}
                      productIndex={productIndex}
                    />
                  </>
                )}

                {isUndoCancelVisible && !uwaState.revising && (
                  <>
                    <ActionButton
                      color="secondary"
                      disabled={!undoCancelAllowed || isUndoCancelButtonDisabled}
                      onClick={() => setUndoCancelDialogOpen(true)}
                      startIcon={<UndoIcon />}
                      variant="outlined"
                    >
                      Undo Cancellation
                    </ActionButton>

                    <CancelUndoCancelUWARequestDialog
                      title="Undo Cancellation?"
                      buttonText="Undo Cancellation"
                      isUndoCancelButton={true}
                      schema={`uwaProducts.${productIndex}`}
                      onSubmit={handleUndoCancelSubmit}
                      isOpen={isUndoCancelDialogOpen}
                      onClose={() => {
                        setUndoCancelDialogOpen(false);
                      }}
                      productIndex={productIndex}

                    />
                  </>
                )}

                {isReviseVisible && !uwaState.revising && (
                  <ActionButton
                    color="secondary"
                    onClick={() => handleRevise(ReviseAction.OpenRevise)}
                    disabled={!reviseAllowed || isReviseButtonDisabled}
                    startIcon={<ReviseIcon />}
                    variant="outlined"
                  >
                    Revise
                  </ActionButton>
                )}

                {isReviseVisible && uwaState.revising && (
                  <>
                    <ActionButton
                      color="secondary"
                      disabled={isFileDisabled}
                      variant="outlined"
                      startIcon={<CancelIconWithForbidden />}
                      onClick={() => {
                        handleRevise(ReviseAction.CancelRevise);
                      }}
                    >
                      Cancel
                    </ActionButton>

                    <ActionButton
                      color="primary"
                      variant="outlined"
                      startIcon={<CheckIcon />}
                      disabled={isSubmitRevisionButtonDisabled}
                      onClick={() => {
                        handleRevise(ReviseAction.SubmitRevise);
                      }}
                    >
                      Submit Revision
                    </ActionButton>

                  </>
                )}
                {isProductStatusVisible && !uwaState.revising && (
                  <ActionButton
                    color="primary"
                    onClick={handleViewRequestSummary}
                    startIcon={<PrinterEyeIcon />}
                    variant="outlined"
                    disabled={!uwaRequestSummaryAllowed}
                  >
                    View Request Summary
                  </ActionButton>
                )}

              </ButtonsContainer>
            </Grid>
          </Grid>
        </GridContainer>
      </CollapsableFormSection>
      <ConfirmationDialogWithProgressbar
        autoClose={true}
        title="Please wait while uploading your documents ..."
        closeButtonText="Done! Click to Continue"
        isOpen={openProgressDialog}
        onClose={handleOnCloseProgressDialog}
        requestId={progressRequestId}
        runProgress={runProgress}
        progressImageType={ProgressImageType.circular}
        delay={1000} // 1000 miliseconds - call processStatus every 1 second
        maxDelayCounter={90} // max delay: 90 x 1000 miliseconds = 90 seconds - close progress maximum in 90 seconds + response time
        maxZeroPctCompleteCount={30} //  close 0 percentage dialog after about 30 seconds + response times for each call; where 30 count x delay (1000 miliseconds ) + response for each call
      />
      <UploadFailedDialog
        ref={uploadFailedDialogRef}
        failedUploadFilesRef={failedUploadFilesRef}
      />

      <PdfViewer
        isOpen={openPdfViewer}
        onClose={() => setOpenPdfViewer(false)}
        pdfDocuments={pdfDocuments}
        isStaticFile={isStaticFile}
      />
      {/* <ErrorDialog
        isOpen={openErrorDialog}
        confirmationMessage={errorMessage}
        correlationId={correlationId}
        onYes={handleOnYesErrorDialog}
      /> */}
    </>
  );

  //#endregion
}
